/**
 * HTL (09-10-23)
 * Functions needed to record Creative events and save them to Google Spreadsheet.
 */
// LIBS
import { analytics_record } from "../libs/amplify";
// TYPES
type SaveUser = {
  id: string;
  teamId: string;
  teamName: string;
  firstName: string;
  lastName: string;
  email: string;
};

export function removeCreativeTTB() {
  localStorage.removeItem("time_to_build_creative");
}

export function setCreativeTTB(key: string, value: string) {
  if (localStorage.getItem("time_to_build_creative") === null) {
    // Create the item with an initial value
    localStorage.setItem("time_to_build_creative", JSON.stringify({}));
  }
  const creative = JSON.parse(localStorage.getItem("time_to_build_creative") as string);

  if (key === "mediaUpload" || key === "mediaCrop") {
    /**
     * We want to grab all instances of media assets, so we append these to the existing data, rather than overwriting each time.
     */
    if (creative[key]) {
      // If [key]" exists, add a new value to it
      creative[key].push(value);
    } else {
      // If [key]" doesn't exist, create a new array
      creative[key] = [value];
    }
    creative["mediaUpload"] = [...creative["mediaUpload"], value];
  } else {
    // Add a new or overwrite key-value pair
    creative[key] = value;
  }

  localStorage.setItem("time_to_build_creative", JSON.stringify(creative));
}

export function saveCreativeTTB(creativeId: string, user: SaveUser) {
  const creative = JSON.parse(localStorage.getItem("time_to_build_creative") as string);
  if (!creative) return;
  if (creativeId !== creative.id) return;

  analytics_record({
    name: "time_to_build",
    attributes: {
      eventType: "creativeBuild",
      id: creative.id,
      week: getWeekNumber(),
      userId: user.id,
      teamId: user.teamId,
      teamName: user.teamName,
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      total: getTimeDiffInMins(creative.endTime, creative.startTime),
      brandlogo: creative?.brandlogo || 0,
      fontUpload: creative?.fontUpload || 0,
      socialFetch: creative?.socialFetch || 0,
      mediaUpload: creative.mediaUpload,
      mediaCrop: creative?.mediaCrop || 0,
      numberOfAssets: creative.mediaUpload.length,
      save: creative.save,
    },
  });
}

function getWeekNumber(): number {
  /**
   * HTL (10-10-23)
   * Returns number referring to current week number of the year.
   */
  let date = new Date();
  // Copy date so don't modify original
  date = new Date(date);
  date.setHours(0, 0, 0, 0);
  /**
   * HTL (10-10-23)
   * Set to nearest Thursday: current date + 4 - current day number
   * This function is based on the ISO 8601 standard for calculating week numbers.
   * ISO 8601 defines that the first week of the year should contain the first Thursday of the year.
   * This ensures that the year's first week has at least four days in the current year and one day in the previous year.
   * This is done to maintain consistency in week numbering.
   */
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  // Get first day of year
  const yearStart = new Date(date.getFullYear(), 0, 1);
  // Calculate full weeks to current date
  const weekNumber = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return weekNumber;
}

function getTimeDiffInMins(isoDateString1: string, isoDateString2: string): string {
  /**
   * HTL (10-10-23)
   * Returns difference between 2 ISO strings in HH:MM:SS format
   */
  function formatTimeDifference(milliseconds: number): string {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // HH:MM:SS format
    const formattedTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

    return formattedTime;
  }
  const date1: Date = new Date(isoDateString1);
  const date2: Date = new Date(isoDateString2);

  const timeDifferenceMilliseconds: number = date2.getTime() - date1.getTime();
  const formattedTimeDifference: string = formatTimeDifference(timeDifferenceMilliseconds);
  return formattedTimeDifference;
}
